import { useMutation, useQueryClient } from "@tanstack/react-query"
import { makeParseDocuments } from "../../domain/use-cases/parse-documents"
import { makeReparseDocuments } from "../../domain/use-cases/reparse-documents"
import { makeGetDocuments } from "../../domain/use-cases/get-documents"
import { httpDocumentRepository } from "../../data/repositories/document-repository"

const parseDocuments = makeParseDocuments(httpDocumentRepository)
const reparseDocuments = makeReparseDocuments(httpDocumentRepository)
const getDocuments = makeGetDocuments(httpDocumentRepository)

export const useParseDocuments = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (docIds: string[]) => parseDocuments(docIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  })
}

export const useReparseDocuments = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (docIds: string[]) => reparseDocuments(docIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  })
}

/** API max page size for GET /documents (see documents.md). */
const LIST_PAGE_LIMIT = 100

/**
 * Max ids per POST /documents/parse request. Must stay <= the backend's
 * `MAX_PARSE_IDS` (500) — the endpoint 422s a payload with more than 500
 * unique ids. "Analyze all" on a large KB therefore sends several requests.
 */
export const PARSE_CHUNK_SIZE = 500

/** Send `ids` through `send` in sequential chunks of at most PARSE_CHUNK_SIZE. */
export const sendAllInChunks = async (
  ids: string[],
  send: (chunk: string[]) => Promise<void>,
) => {
  for (let i = 0; i < ids.length; i += PARSE_CHUNK_SIZE) {
    await send(ids.slice(i, i + PARSE_CHUNK_SIZE))
  }
}

const fetchAllDocumentIds = async (kbId: string) => {
  const first = await getDocuments({ kbId, page: 1, limit: LIST_PAGE_LIMIT })
  if (first.total === 0) return null
  const ids = first.items.map((d) => d.id)
  let page = 2
  while (ids.length < first.total) {
    const batch = await getDocuments({ kbId, page, limit: LIST_PAGE_LIMIT })
    ids.push(...batch.items.map((d) => d.id))
    page += 1
    if (batch.items.length === 0) break
  }
  return ids
}

export const useParseAllDocuments = (kbId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const ids = await fetchAllDocumentIds(kbId)
      if (!ids) return
      await sendAllInChunks(ids, parseDocuments)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  })
}

export const useReparseAllDocuments = (kbId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const ids = await fetchAllDocumentIds(kbId)
      if (!ids) return
      await sendAllInChunks(ids, reparseDocuments)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  })
}
